import { IconMoon, IconSun, cn, useTheme } from '@lb/ui'
import { useTranslation } from '@lb/i18n'

/**
 * A segmented Light / Dark pill, per the approved parent mock.
 *
 * ── Why segmented and not a single toggle ────────────────────────────────
 * Two states, both worth naming. A single "Theme · Light" line requires a
 * click to discover it toggles, and there is no reading of the second
 * state until you're in it. A segmented pill shows both options at once
 * and lets somebody who is on the wrong one press exactly the right one
 * in one tap rather than in two.
 *
 * ── Pinned to the bottom of the sidebar ──────────────────────────────────
 * The mock places it below the profile pill, at the very end of the rail
 * — not folded into the profile menu — so it is a single-click reach and
 * so the profile menu stays about the account rather than about
 * appearance. Same argument the promo-code CTA gets on the confirm
 * screen: put the thing where it is most likely to be looked for.
 */
export function ThemePill() {
  const { theme, toggle } = useTheme()
  const { t } = useTranslation()

  return (
    <div
      role="group"
      aria-label={t('theme.toggle')}
      className="mx-1 mt-2 flex items-center gap-1 rounded-full border border-border bg-cream-dark p-1"
    >
      <ThemeButton
        active={theme === 'light'}
        onClick={() => {
          if (theme !== 'light') toggle()
        }}
        icon={<IconSun size={14} title={null} />}
        label={t('theme.light')}
      />
      <ThemeButton
        active={theme === 'dark'}
        onClick={() => {
          if (theme !== 'dark') toggle()
        }}
        icon={<IconMoon size={14} title={null} />}
        label={t('theme.dark')}
      />
    </div>
  )
}

function ThemeButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex min-h-[32px] flex-1 items-center justify-center gap-1.5 rounded-full px-2 text-[12px] font-medium transition-colors',
        active ? 'bg-surface text-ink shadow-sm' : 'text-muted hover:text-ink',
      )}
    >
      {icon}
      {label}
    </button>
  )
}
